import { useEffect } from 'react';
import { LegacySignInRedirect } from './components/LegacySignInRedirect';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ChildSafety from './pages/ChildSafety';
import DeleteAccount from './pages/DeleteAccount';
import SafetyProtocol from './pages/SafetyProtocol';
import { AUTHORED_PAGES, AuthoredMarketingPage } from './pages/AuthoredPages';
import UseCasePage from './pages/UseCasePage';
import BlogIndex from './pages/BlogIndex';
import BlogPost from './pages/BlogPost';
import GiftPage from './pages/GiftPage';
import CheckPage, { CHECK_PAGES } from './pages/CheckPage';
import { captureChannelFromPath } from './lib/channel';
import { track } from '../../shared-web/track';
import { CookieBanner, type ConsentRegime } from '../../shared-web/CookieBanner';
import { forgetAdCookies } from '../../shared-web/cookie-consent';

/**
 * An unknown path in this bundle. Reachable only client-side (a direct open of
 * the built index.html, mostly in dev) — the edge routes each marketing page
 * explicitly and 404s everything else, so a real visitor never lands here. The
 * product homepage owns `/`; send them there rather than paint anything.
 */
function RedirectHome() {
  useEffect(() => {
    window.location.replace('/');
  }, []);
  return null;
}

// `routePath` is supplied by the build-time prerenderer (entry-server.tsx),
// which has no `window` to read the path from. In the browser it is omitted and
// the live location wins, exactly as before.
function renderPage(routePath?: string) {
  const path = routePath ?? (typeof window === 'undefined' ? '/' : window.location.pathname);
  if (path === '/privacy' || path === '/privacy-policy') return <PrivacyPolicy />;
  if (path === '/terms' || path === '/terms-of-service') return <TermsOfService />;
  // The public account-deletion instructions. A store's data-safety form needs a
  // URL that works without the app and without signing in — /settings is behind
  // the login and /privacy carries the right but not the steps.
  if (path === '/delete-account') return <DeleteAccount />;
  // The published crisis protocol and the AI notice (2026-09-17): what the
  // product does when someone may be at risk, in plain words, without a session.
  if (path === '/safety') return <SafetyProtocol />;
  // The child safety standards (2026-11-03): the URL the Play Console child
  // safety declaration links to — public, no session, names the app and developer.
  if (path === '/child-safety') return <ChildSafety />;
  // The gift page (2026-10-20 founder): give Ember to someone you love.
  if (path === '/gift') return <GiftPage />;
  // The authored pages (pages/AuthoredPages.tsx): the community guidelines;
  // the brand-narrative pages — the footer's Understand entries (2026-08-30
  // footer redesign; copy source is the footer 定稿 in ember-brain v3.0
  // §4.12), of which /share-your-experience and the /ways-to-use-ember hub
  // left the footer with that redesign but stay routable for campaigns and
  // search; and the comparison articles (2026-09-29) with their /compare
  // index (2026-09-30: the articles' breadcrumbs and footers named it from
  // the first day, so it has to be a page).
  // The self-check pages (2026-10-26, pages/CheckPage.tsx): one situation each,
  // ending at its light and at a conversation with Ember.
  const check = CHECK_PAGES[path];
  if (check) return <CheckPage html={check} />;
  const authored = AUTHORED_PAGES[path];
  if (authored) return <AuthoredMarketingPage html={authored} />;
  // Organic-search situation pages. Content rides the same runtime bootstrap
  // the FAQ used to, resolved per request by the edge from the database — the
  // edge has already 404'd an unknown or unpublished slug, so reaching here
  // means there is a page (or a client-side arrival with no bootstrap, which
  // the component answers with its own not-found state).
  if (path.startsWith('/for/')) return <UseCasePage />;
  // Blog. The index, its pagination and the category archives are one
  // component over different slices; only /blog/<slug> is a different page.
  // Order matters: the two prefixed forms have to be tested before the
  // catch-all article match, or /blog/category/x reads as an article slug.
  if (path === '/blog' || path.startsWith('/blog/page/') || path.startsWith('/blog/category/')) {
    return <BlogIndex />;
  }
  if (path.startsWith('/blog/')) return <BlogPost />;
  return <RedirectHome />;
}

/**
 * `routePath` is set only by the build-time prerenderer (entry-server.tsx),
 * which renders each static page once in Node. Everything in this component that
 * needs a browser is guarded on `typeof window` rather than on that prop, so the
 * guards hold for any non-DOM render, not just this one caller.
 */
export default function App({ routePath }: { routePath?: string } = {}) {
  // First-touch: stash any channel slug (+ utm) before rendering so it's
  // available to the door flows that follow. Storage-backed and browser-only;
  // the prerender has no visitor to attribute.
  if (typeof window !== 'undefined') captureChannelFromPath();
  // Which banner copy is true for this visitor. The head snippet in index.html
  // decided it before React mounted and, where it decided yes, has already
  // started the pixels — so this reads what happened rather than guessing at it
  // a second time. A non-DOM render gets the asking version, which is the safe
  // default and never reaches a visitor (the banner is not prerendered).
  const regime: ConsentRegime = typeof window !== 'undefined' && window.__oriConsentRegime === 'opt_out'
    ? 'opt_out'
    : 'opt_in';
  return (
    <>
      <LegacySignInRedirect />
      {renderPage(routePath)}
      {/* The notice belongs on every page this bundle serves, because the head
          snippet starts the ad pixels on every one of them for an undecided US
          visitor — including the campaign and blog pages nobody reaches the
          homepage from (2026-09-11 legal走查, A5; restores what 73f7961d took
          out). `reserveSpace` keeps it off the footer's legal row, which is
          where Privacy, Terms and Cookie preferences live.

          Never prerendered. Whether the banner shows is a fact about THIS
          visitor, read from localStorage in the component's state initializer —
          and the build has no visitor. Prerendering it would bake in the
          "undecided" state, so a returning visitor who already answered would
          see it painted from the server HTML and then vanish when the bundle
          boots. It is not crawlable content, so skipping it costs nothing. */}
      {typeof window === 'undefined' ? null : (
        <CookieBanner
          regime={regime}
          reserveSpace
          // Banner-level telemetry: `shown` is the honest denominator for the
          // accept rate, and the decision beacon records the outcome even for a
          // visitor whose click beacon is lost to the reload that may follow.
          // The rejection beacon does double duty — the server reads it to drop
          // the stored Meta match keys (events-routes.js).
          onShown={() => track('consent.banner_shown', { regime })}
          onDecision={(decision) => track('consent.decided', { decision, regime })}
          // The visitor just opted in — start the ad pixels (defined by the
          // index.html head snippet) without a reload.
          onAccept={() => window.__oriLoadAdPixels?.()}
          // Under opt_out the pixels have already dropped their cookies, so
          // stopping means deleting them, not just remembering the choice.
          onReject={() => forgetAdCookies()}
        />
      )}
    </>
  );
}
